/* ============================================================
   FirstPostReveal — the post-onboarding wow moment (port of
   research-pack/research-12-first-post). Full-viewport stage:
   the generated first post lands in a big post card (same form
   language as Edit Post's live preview) while the right column
   explains it was drafted from the research and strategy.
   Reached from the Setup Journey done-card; both CTAs
   land on the regular post preview where scheduling/editing live.
   Motion: entrance animations are opt-in via
   prefers-reduced-motion: no-preference; a stall fallback adds
   .is-anim-off so the end state always renders.
   ============================================================ */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/Icons.jsx';
import { api } from '../services/api/client.js';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useBrand } from '../contexts/BrandContext.jsx';
import { markFirstPostJourneyVisited } from '../components/dashboard/SetupJourneyWidget.jsx';
import { markFirstPostOpened } from '../lib/firstPostReady.js';
import { shouldWaitForImage } from '../lib/freePreview.js';
import { useFreePreview } from '../contexts/FreePreviewContext.jsx';
import CopyTextButton from '../components/freePreview/CopyTextButton.jsx';
import { isFirstPostOnboarding } from '../lib/freeOnboarding.js';
import { freePublishingState } from '../lib/freePublishing.js';

function unwrap(res) {
  return res?.data ?? res;
}

const REDUCED_MOTION = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export default function FirstPostReveal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { postId } = useParams();
  const { user } = useAuth() || {};
  const brand = useBrand();
  const activeBrandId = brand.enabled ? brand.activeBrandId : null;
  const brandReady = !brand.enabled || (!brand.loading && Boolean(activeBrandId));
  const userId = user?.id ?? null;
  const organizationId = user?.organizationId ?? null;
  const [loadedPost, setPost] = useState(null);
  const post = loadedPost?.id === postId && loadedPost.userId === userId
    && loadedPost.organizationId === organizationId ? loadedPost : null;
  const [failed, setFailed] = useState(false);
  const [readAttempt, setReadAttempt] = useState(0);
  const [animOff, setAnimOff] = useState(REDUCED_MOTION);
  const [typed, setTyped] = useState('');
  const [typingDone, setTypingDone] = useState(REDUCED_MOTION);
  const rootRef = useRef(null);
  const freePreview = useFreePreview();
  const firstPostOnboarding = isFirstPostOnboarding(freePreview.entitlements);
  const ownPublishing = freePublishingState(freePreview.entitlements);

  useEffect(() => {
    if (!userId) return undefined;
    setPost(null);
    setFailed(false);
    let cancelled = false;
    (async () => {
      // The first-post IMAGE is attached a beat after the text. A single fetch
      // on mount used to reveal the "finished" post with an empty image slot —
      // exactly the jarring moment we want to avoid. Poll until the image is
      // present before revealing; bounded so a genuinely image-less post (e.g.
      // image gen failed) still reveals after the deadline instead of hanging.
      const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
      const deadline = Date.now() + 60000;
      while (!cancelled) {
        let raw;
        try {
          raw = unwrap(await api.content.get(postId));
        } catch (_) {
          if (Date.now() > deadline) { if (!cancelled) setFailed(true); return; }
          await sleep(2500);
          continue;
        }
        if (cancelled) return;
        const item = raw?.contentItem || raw || {};
        if (item.id !== postId || item.userId !== userId || item.organizationId !== organizationId) {
          setFailed(true);
          return;
        }
        const version = raw?.activeVersion || null;
        const caption = String(version?.body || version?.caption || item.body || '').trim();
        const imageUrl = version?.imageUrl || raw?.mediaPreview?.url || (Array.isArray(raw?.mediaUrls) ? raw.mediaUrls[0] : null) || null;
        if (firstPostOnboarding && (item.archivedAt || ['deleted', 'archived'].includes(item.status)
          || item.generationStatus === 'failed' || raw?.jobVisualStatus === 'failed'
          || (!imageUrl && Date.now() > deadline))) {
          setFailed(true);
          return;
        }
        // GH-1133 S5 (§3.9 item 10): a text_post has no image to wait for —
        // reveal at once instead of holding the curtain for 60 s. Every other
        // format waits exactly as before.
        if ((!firstPostOnboarding || imageUrl) && (!shouldWaitForImage({ format: item.format, imageUrl }) || Date.now() > deadline)) {
          setPost({
            id: item.id,
            userId: item.userId,
            organizationId: item.organizationId,
            brandProfileId: item.brandProfileId,
            readyForReceipt: ['ready', 'draft', 'scheduled', 'published'].includes(item.status)
              && item.generationStatus !== 'failed'
              && !['queued', 'processing', 'generating', 'visualizing', 'pending', 'failed'].includes(String(raw?.jobVisualStatus || ''))
              && (item.format !== 'image_post' || Boolean(imageUrl)),
            title: String(version?.hook || item.title || caption.split('\n')[0] || '').slice(0, 80),
            caption,
            imageUrl,
            author: item.businessName || null,
          });
          return;
        }
        // Text is ready but the image is still rendering — hold the curtain
        // (keep `post` null → loading state) and poll again.
        await sleep(2500);
      }
    })();
    return () => { cancelled = true; };
  }, [postId, userId, organizationId, activeBrandId, firstPostOnboarding, readAttempt]);

  // Retire the dashboard first-post journey card now that the user has actually
  // landed here and the post has loaded. User-scoped — wait for a real userId so
  // we never mark the 'anon' bucket. Until this fires the card persists across
  // reloads, so the entry point is never lost before the user opens the post.
  useEffect(() => {
    if (post && userId && post.id === postId) {
      markFirstPostJourneyVisited(userId);
      // A bell may intentionally open another brand's owned post. Keep that
      // deep link working, but don't consume the active brand's ready receipt.
      if (post.readyForReceipt && (!brand.enabled || (brandReady && post.brandProfileId === activeBrandId))) {
        markFirstPostOpened(userId, post.id);
      }
    }
  }, [post, userId, postId, brand.enabled, brandReady, activeBrandId]);

  // Stall fallback: if the entrance animations never ran (old engine, hidden
  // tab), force the end state so the screen is never blank.
  useEffect(() => {
    const timer = setTimeout(() => {
      const el = rootRef.current?.querySelector('.fpr-ph');
      if (!el) return;
      try {
        const running = el.getAnimations().some((a) => a.playState === 'running' || (a.currentTime ?? 0) > 0);
        if (!running && getComputedStyle(el).opacity === '0') setAnimOff(true);
      } catch (_) {
        if (getComputedStyle(el).opacity === '0') setAnimOff(true);
      }
    }, 1800);
    return () => clearTimeout(timer);
  }, [post]);

  // Typed caption (skipped entirely under reduced motion). The card is
  // Edit-Post-preview sized now, so it comfortably fits a longer excerpt.
  const captionToType = post?.caption ? post.caption.slice(0, 300) : '';
  useEffect(() => {
    if (!captionToType) return undefined;
    if (REDUCED_MOTION || animOff) {
      setTyped(captionToType);
      setTypingDone(true);
      return undefined;
    }
    let index = 0;
    setTyped('');
    setTypingDone(false);
    const intervalRef = { current: null };
    const start = setTimeout(() => {
      intervalRef.current = setInterval(() => {
        index += 1;
        setTyped(captionToType.slice(0, index));
        if (index >= captionToType.length) {
          clearInterval(intervalRef.current);
          setTypingDone(true);
        }
      }, 22);
    }, 1300);
    return () => {
      clearTimeout(start);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [captionToType, animOff]);

  const openPost = () => navigate(`/app/post/${encodeURIComponent(postId)}`);
  const wallMeta = () => ({ postId, thumbUrl: post?.imageUrl || null, thumbTitle: post?.title || null });
  const onSchedule = freePreview.active && !ownPublishing ? () => freePreview.openTrialWall('schedule', wallMeta()) : openPost;
  const onEdit = ownPublishing
    ? () => navigate(`/app/post/${encodeURIComponent(postId)}/edit`)
    : freePreview.active
      ? freePreview.mode === 'active' && !freePreview.readOnly
        ? () => navigate(`/app/post/${encodeURIComponent(postId)}?edit=caption`)
        : () => freePreview.openTrialWall('edit', wallMeta())
      : openPost;

  const initial = useMemo(() => (post?.author || 'D')[0].toUpperCase(), [post]);

  if (failed) {
    if (firstPostOnboarding) return <section className="card" role="alert">
      <p>{t('freePublishing.loadError')}</p>
      <button type="button" className="btn btn-primary" onClick={() => { api.invalidateCache?.(['/api/content']); setReadAttempt(value => value + 1); }}>{t('common.retry')}</button>
      <button type="button" className="btn btn-ghost" onClick={() => navigate('/app/library')}>{t('nav.library')}</button>
    </section>;
    // The post fetch failed — no theater, back to the dashboard.
    return <Navigate to="/app" replace />;
  }

  if (!post) {
    // Hold the curtain until the image is attached (the poll above sets `post`
    // only once the image is present, or after the deadline). Never reveal a
    // finished-looking post with an empty image slot.
    return (
      <div className={`fpr-stage ${animOff ? 'is-anim-off' : ''}`}>
        <div className="fpr-bg" aria-hidden="true" />
        <div style={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }} role="status" aria-live="polite">
          <p style={{ color: 'var(--text-muted, #9a9aa2)', fontSize: 15, letterSpacing: '0.01em' }}>
            {t('reveal.finishing', 'Putting your first post together…')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`fpr-stage ${animOff ? 'is-anim-off' : ''}`} ref={rootRef}>
      <div className="fpr-bg" aria-hidden="true" />
      <div className="fpr-reveal">
        <div className="fpr-ph-wrap">
          <div className="fpr-halo" aria-hidden="true" />
          <div className="fpr-ph">
            <div className="fpr-author">
              <span className="fpr-av">{initial}</span>
              <span className="fpr-au-n">{post?.author || t('reveal.yourBrand')}</span>
            </div>
            <div className="fpr-img" style={post?.imageUrl ? { backgroundImage: `url(${post.imageUrl})` } : undefined}>
              {!post?.imageUrl && <span className="fpr-img-cap">{post?.title}</span>}
            </div>
            <div className="fpr-acts" aria-hidden="true">
              <Icon name="heart" size={18} /><Icon name="send" size={18} />
            </div>
            <div className="fpr-text">
              {typed}
              {!typingDone && <span className="fpr-cursor" aria-hidden="true" />}
            </div>
          </div>
        </div>
        <div className="fpr-rv">
          <div className="fpr-chain">
            <span className="fpr-ck"><Icon name="check" size={10} /></span><i />
            <span className="fpr-ck"><Icon name="check" size={10} /></span><i />
            <span className="fpr-ck"><Icon name="check" size={10} /></span>
            <span className="fpr-chain-l">{t('reveal.chainLabel')}</span>
          </div>
          <h1 className="fpr-h1">{t('reveal.headline')} <span className="cpr-ac">{t('reveal.headlineAccent')}</span></h1>
          <p className="fpr-lede">{t('reveal.lede')}</p>
          <span className="fpr-draft">{t('reveal.draftBadge')}</span>
          <div className="fpr-acts-row">
            <button type="button" className="btn btn-primary fpr-b" onClick={onSchedule}>
              <Icon name="calendar" size={14} /> {t('reveal.scheduleCta')}
            </button>
            <button type="button" className="btn btn-secondary fpr-b" onClick={onEdit}>
              <Icon name="edit" size={14} /> {t('reveal.editCta')}
            </button>
          </div>
          {freePreview.active ? (
            <div className="fp-draft-note">
              {!ownPublishing && <p className="fp-lockline"><Icon name="lock" size={12} /> {t('freePreview.actionHint')}</p>}
              <div className="fp-draft-note-row">
                <CopyTextButton text={post?.caption || ''} index={0} />
                <button type="button" className="fpb-link" onClick={() => navigate(isFirstPostOnboarding(freePreview.entitlements) ? '/app/library' : '/app')}>
                  {t(firstPostOnboarding ? 'nav.library' : 'freePreview.seeOtherDrafts')} <Icon name="arrowRight" size={12} />
                </button>
              </div>
            </div>
          ) : (
            <p className="fpr-note">{t('reveal.footnote')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
